import styled from 'styled-components';
import { Segment, Dropdown } from 'semantic-ui-react';
import Button_T from '../../../../common/components/atoms/Button';
import { ToggleOn, ToggleOff } from '../../../../common/images/FaIcons';
import colors from '../../../../common/styles/colors';

export const EventSection = styled.div`
  margin-top: 2em;
`;

export const EventSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1em;
`;

export const EventSectionTitle = styled.h3`
  font-size: 1.2em;
  font-weight: 600;
  margin: 0;
  color: ${colors.black};
`;

export const EnabledBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.3em 0.8em;
  border-radius: 4px;
  font-size: 0.85em;
  font-weight: 600;
  margin-left: 1em;
  background-color: ${props => props.enabled ? `${props.theme.primaryColor}40` : colors.greyL};
  color: ${props => props.enabled ? props.theme.primaryColor : colors.greyXD};
`;

export const WarningBox = styled.div`
  display: flex;
  align-items: center;
  padding: 1em;
  margin-bottom: 1em;
  border-radius: 4px;
  background-color: ${colors.warningBackground};
  border: 1px solid ${colors.warningYellow};
  color: ${colors.greyXXD};
  font-size: 0.9em;
`;

export const WarningIcon = styled.span`
  margin-right: 0.5em;
  color: ${colors.warningYellow};
  font-size: 1.2em;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 3em 2em;
  color: ${colors.greyXD};
`;

export const EmptyStateIcon = styled.div`
  font-size: 3em;
  margin-bottom: 0.5em;
  opacity: 0.3;
`;

export const EmptyStateText = styled.p`
  font-size: 0.95em;
  margin: 0.5em 0;
`;

export const EventConfigCardContainer = styled(Segment)`
  &&& {
    padding: 1em;
    margin-bottom: 0.75em;
    border: 1px solid ${colors.greyL};
    border-radius: 4px;
    background-color: ${colors.white};
    box-shadow: none;
    
    &:hover {
      border-color: ${colors.grey};
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }
  }
`;

export const ConditionsList = styled.div`
  margin-bottom: 0.75em;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const ConditionItem = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5em;
  margin-bottom: 0.5em;
  word-break: break-word;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    gap: 0.4em;
  }
`;

export const Badge = styled.span`
  display: inline-block;
  padding: 0.25em 0.6em;
  border-radius: 3px;
  font-size: 0.85em;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  
  ${props => props.variant === 'metric' && `
    background-color: ${props.theme.primaryColor}20;
    color: ${props.theme.primaryColor};
  `}
  
  ${props => props.variant === 'parameter' && `
    background-color: ${colors.greyXXL};
    color: ${colors.greyXXD};
  `}
  
  ${props => props.variant === 'operator' && `
    background-color: ${colors.greyXL};
    color: ${colors.greyXD};
    border: 1px solid ${colors.grey};
  `}
  
  ${props => props.variant === 'value' && `
    background-color: ${colors.white};
    color: ${colors.black};
    border: 1px solid ${colors.greyL};
    word-break: break-all;
    white-space: normal;
  `}
  
  ${props => props.variant === 'logic' && `
    background-color: ${props.logic === 'AND' ? 'rgba(78, 137, 244, 0.1)' : 'rgba(240, 192, 57, 0.1)'};
    color: ${props.logic === 'AND' ? colors.blue : colors.yellow};
    font-weight: 600;
  `}
  
  @media (max-width: 768px) {
    font-size: 0.8em;
    padding: 0.2em 0.5em;
  }
`;

export const ActionRow = styled.div`
  display: flex;
  align-items: center;
  padding-top: 0.75em;
  margin-top: 0.75em;
  border-top: 1px solid ${colors.greyL};
  font-size: 0.9em;
`;

export const ActionLabel = styled.span`
  color: ${colors.greyXD};
  margin-right: 0.5em;
`;

export const ActionValue = styled.span`
  font-weight: 600;
  color: ${colors.black};
`;

export const RangeContainer = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5em;
  flex-wrap: wrap;
`;

const getBorderColor = (variant) => {
  if (variant === 'min') return 'rgba(78, 137, 244, 0.3)';
  if (variant === 'max') return 'rgba(255, 140, 0, 0.3)';
  return colors.greyL;
};

const getBackgroundColor = (variant) => {
  if (variant === 'min') return 'rgba(78, 137, 244, 0.1)';
  if (variant === 'max') return 'rgba(255, 140, 0, 0.1)';
  return colors.greyXXL;
};

const getLabelColor = (variant) => {
  if (variant === 'min') return colors.blue;
  if (variant === 'max') return colors.orange;
  return colors.greyXD;
};

export const LimitContainer = styled.span`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${props => getBorderColor(props.variant)};
  border-radius: 4px;
  background-color: ${props => getBackgroundColor(props.variant)};
  padding: 0.15em 0.5em;
`;

export const LimitLabel = styled.span`
  font-weight: 600;
  color: ${props => getLabelColor(props.variant)};
  margin-right: 0.5em;
  font-size: 0.85em;
  text-transform: uppercase;
`;

export const AxisGroup = styled.span`
  display: inline-flex;
  gap: 0.75em;
`;

export const AxisItem = styled.span`
  color: ${colors.black};
  font-weight: 500;
  font-size: 0.9em;
`;

export const RangeColumnHeader = styled.span`
  font-size: 0.75em;
  font-weight: 500;
  color: ${colors.greyD};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: center;
  padding: 0.2em 0;
`;

export const SingleValueContainer = styled.span`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${colors.greyL};
  border-radius: 4px;
  background-color: rgba(78, 137, 244, 0.05);
  padding: 0.15em 0.5em;
`;

export const AxisBoxContainer = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25em;
  margin: 0 0.25em;
`;

export const AxisBox = styled.span`
  display: inline-block;
  padding: 0.15em 0.4em;
  border-radius: 3px;
  font-size: 0.85em;
  background-color: ${colors.greyXXL};
  border: 1px solid ${colors.greyL};
  color: ${colors.greyXXD};
  font-weight: 500;
`;

export const EditorContainer = styled.div`
  margin-top: 1em;
`;

export const EditorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5em;
`;

export const EditorTitle = styled.h3`
  font-size: 1.1em;
  font-weight: 600;
  margin: 0;
  color: ${colors.black};
`;

export const EditorActions = styled.div`
  display: flex;
  gap: 0.5em;
`;

export const EnableToggleContainer = styled(Segment)`
  &&& {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1em;
    margin-bottom: 1.5em;
    background-color: ${colors.greyXXL};
    border: 1px solid ${colors.greyL};
  }
`;

export const ToggleLabel = styled.div``;

export const ToggleLabelText = styled.div`
  font-weight: 600;
  font-size: 0.95em;
  color: ${colors.black};
`;

export const ToggleLabelSubtext = styled.div`
  font-size: 0.85em;
  color: ${colors.greyXD};
  margin-top: 0.25em;
`;

export const ToggleSwitch = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  outline: none;
  
  &:focus {
    outline: 2px solid ${props => props.theme.primaryColor};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

export const StyledToggleOn = styled(ToggleOn)`
  color: ${props => props.theme.primaryColor || colors.greenD};
  font-size: 1.5em;
  cursor: pointer;
`;

export const StyledToggleOff = styled(ToggleOff)`
  color: ${colors.greyD};
  font-size: 1.5em;
  cursor: pointer;
`;

export const EventsListContainer = styled.div`
  margin-top: 1em;
`;

export const EventsListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1em;
`;

export const EventsListLabel = styled.label`
  font-weight: 600;
  font-size: 0.95em;
  color: ${colors.black};
`;

export const EmptyEventsPlaceholder = styled.div`
  text-align: center;
  padding: 2em;
  border: 2px dashed ${colors.grey};
  border-radius: 4px;
  color: ${colors.greyXD};
`;

export const EventCard = styled(Segment)`
  &&& {
    padding: 1.25em;
    margin-bottom: 1em;
    border: 2px solid ${props => props.hasErrors ? colors.redL : colors.greyL};
    border-radius: 4px;
    background-color: ${props => props.hasErrors ? colors.errorBackground : colors.white};
  }
`;

export const EventCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1em;
`;

export const EventCardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5em;
  font-weight: 600;
  font-size: 0.9em;
  color: ${colors.greyXXD};
`;

export const TreeRoot = styled.span`
  color: ${colors.greenD};
  font-size: 1.2em;
  font-weight: bold;
  margin-right: 0.5em;
  line-height: 1;
`;

export const IconText = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5em;
`;

export const ConditionsSection = styled.div`
  margin-bottom: 1em;
`;

export const ConditionLogicRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5em;
  margin-bottom: 0.75em;
  font-size: 0.9em;
  color: ${colors.greyXD};
`;

export const ConditionRowsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75em;
  margin-bottom: 0.75em;
  margin-left: 1.25em;
  padding-left: 1.75em;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 2px;
    border-left: 2px dashed ${colors.greyD};
  }
`;

export const ConditionRowContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5em;
  padding: 0.75em;
  padding-top: ${props => props.isCoupled ? '2em' : '0.5em'};
  padding-left: 0.5em;
  border-radius: 4px;
  border: 1px solid ${(props) => {
    if (props.hasError) return colors.redL;
    if (props.isCoupled) return colors.greyM;
    return colors.greyL;
  }};
  background-color: ${(props) => {
    if (props.hasError) return colors.errorBackground;
    if (props.isCoupled) return colors.greyXL;
    return colors.greyXXXL;
  }};
  position: relative;
  
  /* Horizontal branch connector - extends to and overlaps vertical line */
  &::before {
    content: '';
    position: absolute;
    left: -1.75em;
    top: 50%;
    width: 1.77em;
    height: 0;
    border-top: 2px dashed ${colors.greyD};
    transform: translateY(-1px);
  }
  
  /* Vertical connector from vertical line down through gap to next item */
  &::after {
    content: '';
    position: absolute;
    left: -1.75em;
    top: 50%;
    bottom: -0.75em;
    width: 0;
    border-left: 2px dashed ${colors.greyD};
  }
  
  /* Mask for vertical line below the last item */
  &:last-child::after {
    content: '';
    position: absolute;
    left: -1.77em;
    top: 50%;
    bottom: -1em;
    width: 4px;
    background-color: ${colors.white};
    border-left: none;
  }
  
  &:hover {
    background-color: ${props => props.hasError ? colors.diffRedXL : colors.white};
    border-color: ${props => props.hasError ? colors.red : colors.grey};
  }
  
  @media (max-width: 768px) {
    padding: 0.5em;
    padding-top: 0.4em;
    gap: 0.4em;
  }
`;

export const ConditionInputGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5em;
  
  @media (max-width: 768px) {
    gap: 0.4em;
  }
`;

export const ThresholdInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3em;
  
  @media (max-width: 768px) {
    gap: 0.25em;
  }
`;

export const ConditionLabel = styled.label`
  font-size: 0.85em;
  font-weight: 600;
  color: ${colors.greyXD};
  min-width: 1.2em;
  text-align: right;
  margin-bottom: 0;
  
  @media (max-width: 768px) {
    font-size: 0.8em;
    min-width: 1em;
  }
`;

export const ConditionUnit = styled.span`
  font-size: 0.85em;
  color: ${colors.greyXD};
  margin-left: 0.25em;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 0.8em;
  }
`;

export const ActionSection = styled.div`
  padding-top: 1em;
  margin-top: 1em;
  border-top: 1px solid ${colors.greyL};
  display: flex;
  align-items: center;
  gap: 1em;
`;

export const ActionSectionLabel = styled.label`
  font-size: 0.9em;
  color: ${colors.greyXD};
`;

export const Button = styled(Button_T)`
  && {
    ${props => props.variant === 'outline' && `
      background-color: ${colors.white};
      border: 1px solid ${colors.greyL};
      color: ${colors.black};
      
      &:hover {
        border-color: ${colors.grey};
        background-color: ${colors.greyXXL};
      }
    `}
    
    ${props => props.variant === 'primary' && `
      background-color: ${props.theme.primaryColor};
      color: ${colors.white};
      border: none;
      
      &:hover {
        background-color: ${props.theme.primaryColor};
        filter: brightness(0.9);
      }
    `}
    
    ${props => props.variant === 'ghost' && `
      background-color: transparent;
      border: none;
      color: ${colors.greyXD};
      padding: 0.25em 0.5em;
      
      &:hover {
        background-color: ${colors.greyXXL};
        color: ${colors.red};
      }
    `}
    
    ${props => props.size === 'sm' && `
      font-size: 0.85em;
      padding: 0.5em 1em;
    `}
    
    ${props => props.disabled && `
      opacity: 0.5;
      cursor: not-allowed;
      
      &:hover {
        background-color: ${props.variant === 'primary' ? props.theme.primaryColor : colors.white};
      }
    `}
  }
`;

export const RemoveButton = styled(Button_T)`
  && {
    position: absolute;
    top: 0.1em;
    right: 0.1em;
    background-color: transparent;
    border: none;
    color: ${colors.greyXD};
    padding: 0.2em 0.3em;
    font-size: 0.9em;
    min-width: auto;
    width: auto;
    height: auto;
    line-height: 1;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      background-color: ${colors.greyXXL};
      color: ${colors.red};
    }
    
    @media (max-width: 768px) {
      top: 0.15em;
      right: 0.15em;
      padding: 0.1em 0.3em;
      font-size: 0.85em;
    }
  }
`;

export const DropdownContainer = styled.div`
  display: inline-block;
  min-width: ${props => props.width || '140px'};
  
  .ui.dropdown {
    height: 32px;
    font-size: 0.9em;
    
    .menu {
      max-height: 300px;
      overflow-y: auto;
    }
  }
`;

export const InfoText = styled.span`
  font-size: 0.85em;
  color: ${colors.greyXD};
  margin-left: 0.5em;
`;

export const AxisSelectorContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: 1em;
`;

export const AxisToggleButtonGroup = styled.div`
  display: flex;
  border: 1px solid ${colors.greyL};
  border-radius: 4px;
  overflow: hidden;
  margin-left: 0.5em;
`;

export const AxisToggleButton = styled.button`
  background-color: ${props => props.selected ? `${props.theme.primaryColor}20` : colors.white};
  color: ${props => props.selected ? props.theme.primaryColor : colors.greyXD};
  border: none;
  border-right: 1px solid ${colors.greyL};
  padding: 0.4em 0.8em;
  font-size: 0.85em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 2.5em;

  &:last-child {
    border-right: none;
  }

  &:hover {
    background-color: ${props => props.selected ? `${props.theme.primaryColor}30` : colors.greyXXL};
  }

  &:focus {
    outline: none;
  }
`;

// Modern Natural Language Design Components
export const NaturalConditionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: ${colors.white};
  padding: 8px;
  border-radius: 8px;
  border: 1px solid ${colors.greyL};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  font-size: 0.9em;

  &:hover {
    border-color: ${colors.grey};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  }

  @media (max-width: 768px) {
    gap: 8px;
    padding: 6px;
    font-size: 0.85em;
  }
`;

export const ConditionLogicPrefix = styled.div`
  /* No monospace family: it has no CJK coverage, and localized connectors
     (ja "または") are wider than the old fixed 36px box, so min-width not width. */
  font-size: 0.95em;
  color: ${colors.greyD};
  font-weight: 600;
  min-width: 36px;
  text-align: right;
  flex-shrink: 0;
  white-space: nowrap;

  @media (max-width: 768px) {
    min-width: 32px;
    font-size: 0.85em;
  }
`;

export const SensorIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background-color: ${props => `${props.theme.primaryColor}20`};
  color: ${colors.black};
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 768px) {
    width: 28px;
    height: 28px;

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

export const SensorNameText = styled.span`
  font-weight: 700;
  color: ${colors.black};
  font-size: 1em;
  
  @media (max-width: 768px) {
    font-size: 0.95em;
  }
`;

export const MetricPillBadge = styled.span`
  display: inline-block;
  font-size: 0.85em;
  font-weight: 500;
  color: ${colors.greyXXD};
  background-color: ${colors.greyXXL};
  padding: 3px 8px;
  border-radius: 12px;
  border: 1px solid ${colors.greyL};
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 0.75em;
    padding: 2px 6px;
  }
`;

export const OperatorBadge = styled.span`
  display: inline-block;
  font-size: 0.8em;
  font-weight: 700;
  /* No uppercase/letter-spacing: operator labels are now localized words
     (de "Außerhalb des Bereichs"), not short English tokens like "OUTSIDE". */
  padding: 3px 8px;
  border-radius: 3px;
  white-space: nowrap;
  background-color: rgba(245, 158, 11, 0.1);
  color: #d97706;
  border: 1px solid rgba(245, 158, 11, 0.3);

  @media (max-width: 768px) {
    font-size: 0.7em;
    padding: 2px 6px;
  }
`;

export const ValueDisplayBox = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: #f8fafc;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  
  @media (max-width: 768px) {
    padding: 4px 6px;
  }
`;

export const MinMaxGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const getMinMaxBgColor = (variant) => {
  if (variant === 'min') return 'rgba(59, 130, 246, 0.05)';
  if (variant === 'max') return 'rgba(249, 115, 22, 0.05)';
  return 'rgba(99, 102, 241, 0.05)';
};

const getMinMaxBorderColor = (variant) => {
  if (variant === 'min') return 'rgba(59, 130, 246, 0.2)';
  if (variant === 'max') return 'rgba(249, 115, 22, 0.2)';
  return colors.greyL;
};

export const MinMaxBox = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: ${props => getMinMaxBgColor(props.variant)};
  border: 1px solid ${props => getMinMaxBorderColor(props.variant)};
  border-radius: 4px;
  padding: 3px 6px;

  @media (max-width: 768px) {
    padding: 2px 5px;
    gap: 4px;
  }
`;

const getMinMaxLabelColor = (variant) => {
  if (variant === 'min') return '#3b82f6';
  if (variant === 'max') return '#f97316';
  return colors.greyD;
};

export const MinMaxLabel = styled.span`
  font-size: 0.75em;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: ${props => getMinMaxLabelColor(props.variant)};

  @media (max-width: 768px) {
    font-size: 0.65em;
  }
`;

export const AxisValueGroup = styled.div`
  display: inline-flex;
  gap: 8px;
  
  @media (max-width: 768px) {
    gap: 6px;
  }
`;

export const AxisValueItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 0.8em;
  color: ${colors.black};
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 0.7em;
  }
`;

export const AxisLabel = styled.span`
  color: #94a3b8;
  margin-right: 2px;
  font-weight: 400;
`;

export const NaturalLanguageText = styled.span`
  font-size: 0.9em;
  color: ${colors.greyD};

  @media (max-width: 768px) {
    font-size: 0.8em;
  }
`;

export const ModernActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  margin-top: 12px;
  border-top: 1px solid ${colors.greyL};
  font-size: 0.9em;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

export const ActionIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: rgba(16, 185, 129, 0.1);
  color: #10b981;
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

export const ActionText = styled.span`
  font-weight: 600;
  font-size: 0.9em;
  color: ${colors.black};
  
  @media (max-width: 768px) {
    font-size: 0.85em;
  }
`;

export const ValidationError = styled.div`
  display: flex;
  align-items: flex-start;
  padding: ${props => props.small ? '0.4em 0.6em' : '0.6em 0.8em'};
  margin-bottom: ${props => props.small ? '0.3em' : '0.5em'};
  border-radius: 4px;
  background-color: ${colors.errorBackground};
  border: 1px solid ${colors.redL};
  color: ${colors.errorRed};
  font-size: ${props => props.small ? '0.8em' : '0.85em'};
  line-height: 1.4;
  
  &::before {
    content: '⚠ ';
    margin-right: 0.4em;
    font-weight: bold;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

// Coupling Control Styles
export const CouplingSection = styled.div`
  padding: 1em;
  margin-bottom: 1.5em;
  border: 2px solid ${colors.greyL};
  border-radius: 8px;
  background-color: ${colors.greyXL};
`;

export const CouplingHeader = styled.div`
  margin-bottom: 0.5em;
`;

export const CouplingCheckbox = styled.div`
  display: flex;
  align-items: center;
  
  .ui.checkbox label {
    font-weight: 600;
    font-size: 1em;
    color: ${colors.black};
  }
`;

export const CouplingDescription = styled.p`
  margin: 0.5em 0 1em 1.8em;
  font-size: 0.9em;
  color: ${colors.greyXD};
  font-style: italic;
`;

export const CouplingConfigSection = styled.div`
  margin-top: 1em;
  padding-top: 1em;
  border-top: 1px solid ${colors.greyL};
`;

export const ConfigRow = styled.div`
  display: flex;
  margin-bottom: 1.2em;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

export const ConfigLabel = styled.div`
  min-width: 140px;
  font-weight: 600;
  color: ${colors.greyXXD};
  padding-top: 0.3em;
`;

export const ConfigValue = styled.div`
  flex: 1;
  
  .ui.checkbox {
    display: inline-flex;
    align-items: center;
  }
`;

export const AlarmThresholdDisplay = styled.div`
  margin-top: 1.2em;
  padding: 1em;
  background-color: ${colors.white};
  border: 1px solid ${colors.greyL};
  border-radius: 4px;
`;

export const AlarmThresholdHeader = styled.div`
  font-weight: 600;
  color: ${colors.greyXXD};
  margin-bottom: 0.8em;
  font-size: 0.95em;
`;

export const AlarmThresholdItem = styled.div`
  padding: 0.4em 0;
  font-size: 0.9em;
  color: ${colors.greyXD};
  
  strong {
    color: ${colors.black};
  }
`;

// Modern Coupling UI Components

export const ModernToggleSwitch = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  user-select: none;
`;

export const ToggleSwitchTrack = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 36px;
  height: 20px;
  border-radius: 10px;
  background-color: ${props => props.checked ? props.theme.primaryColor : colors.grey};
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${props => props.checked ? props.theme.primaryColor : colors.greyD};
    filter: ${props => props.checked ? 'brightness(0.9)' : 'none'};
  }
`;

export const ToggleSwitchThumb = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: ${colors.white};
  transform: ${props => props.checked ? 'translateX(19px)' : 'translateX(3px)'};
  transition: transform 0.2s ease;
`;

export const ToggleSwitchLabel = styled.span`
  font-size: 0.95em;
  font-weight: 600;
  color: ${props => props.checked ? colors.black : colors.greyXD};
  transition: color 0.2s ease;
`;

export const LinkedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background-color: ${props => `${props.theme.primaryColor}20`};
  border: 1px solid ${props => `${props.theme.primaryColor}40`};
  border-radius: 4px;
  font-size: 0.75em;
  font-weight: 700;
  color: ${props => props.theme.primaryColor};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

export const SensorSelectionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 0;
`;

export const SensorSelectionCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid ${props => props.selected ? `${props.theme.primaryColor}60` : colors.greyL};
  background-color: ${props => props.selected ? colors.white : 'rgba(247, 247, 247, 0.5)'};
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${props => props.selected ? `0 1px 3px ${props.theme.primaryColor}20` : 'none'};
  
  ${props => props.selected && `
    ring: 1px solid ${props.theme.primaryColor}40;
  `}
  
  &:hover {
    border-color: ${props => props.selected ? props.theme.primaryColor : colors.grey};
    background-color: ${props => props.selected ? colors.white : colors.greyXL};
  }
`;

export const SensorCardLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

export const SensorCheckIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 2px solid ${props => props.checked ? props.theme.primaryColor : colors.grey};
  background-color: ${props => props.checked ? props.theme.primaryColor : colors.white};
  transition: all 0.2s ease;
  
  svg {
    width: 14px;
    height: 14px;
    color: ${colors.white};
    opacity: ${props => props.checked ? 1 : 0};
    transition: opacity 0.2s ease;
  }
  
  ${SensorSelectionCard}:hover & {
    border-color: ${props => props.checked ? props.theme.primaryColor : colors.greyD};
  }
`;

export const SensorIconBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 6px;
  background-color: ${(props) => {
    if (!props.selected) return colors.greyXL;
    if (props.sensorType === 'temperature') return 'rgba(255, 92, 92, 0.1)';
    if (props.sensorType === 'vibration') return `${props.theme.primaryColor}20`;
    return colors.greyXL;
  }};
  transition: background-color 0.2s ease;
  
  svg {
    width: 16px;
    height: 16px;
    color: ${(props) => {
    if (!props.selected) return colors.greyD;
    if (props.sensorType === 'temperature') return colors.redL;
    if (props.sensorType === 'vibration') return props.theme.primaryColor;
    return colors.greyD;
  }};
  }
`;

export const SensorCardLabel = styled.span`
  font-size: 0.9em;
  font-weight: 500;
  color: ${props => props.selected ? colors.black : colors.greyXD};
  transition: color 0.2s ease;
`;

export const FeatureDropdownButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 150px;
  padding: 6px 12px;
  font-size: 0.85em;
  font-weight: 500;
  color: ${colors.black};
  background-color: ${colors.white};
  border: 1px solid ${colors.greyL};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  
  &:hover {
    border-color: ${props => props.theme.primaryColor};
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primaryColor};
    box-shadow: 0 0 0 2px ${props => `${props.theme.primaryColor}20`};
  }
  
  svg {
    width: 14px;
    height: 14px;
    color: ${colors.greyD};
    flex-shrink: 0;
  }
`;

export const FeatureDropdownMenu = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  min-width: 224px;
  background-color: ${colors.white};
  border: 1px solid ${colors.greyL};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 20;
  overflow: hidden;
  animation: fadeIn 0.1s ease;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

export const FeatureDropdownHeader = styled.div`
  padding: 8px 12px;
  font-size: 0.75em;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${colors.greyD};
  background-color: rgba(247, 247, 247, 0.3);
  border-bottom: 1px solid rgba(229, 232, 225, 0.5);
`;

export const FeatureCheckboxItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  background-color: ${props => props.disabled ? 'rgba(247, 247, 247, 0.5)' : 'transparent'};
  
  &:hover {
    background-color: ${props => props.disabled ? 'rgba(247, 247, 247, 0.5)' : colors.greyXXL};
  }
`;

export const FeatureCheckbox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 2px solid ${props => props.checked ? props.theme.primaryColor : colors.grey};
  background-color: ${props => props.checked ? props.theme.primaryColor : colors.white};
  transition: all 0.2s ease;
  
  svg {
    width: 10px;
    height: 10px;
    color: ${colors.white};
  }
  
  ${FeatureCheckboxItem}:hover & {
    border-color: ${(props) => {
    if (props.checked) return props.theme.primaryColor;
    if (props.disabled) return colors.grey;
    return props.theme.primaryColor;
  }};
  }
`;

export const FeatureCheckboxLabel = styled.span`
  flex: 1;
  font-size: 0.85em;
  font-weight: 500;
  color: ${props => props.checked ? colors.black : colors.greyXD};
`;

export const FeatureUnavailableBadge = styled.span`
  font-size: 0.7em;
  font-weight: 700;
  background-color: ${colors.greyL};
  color: ${colors.greyXD};
  padding: 2px 6px;
  border-radius: 3px;
`;

export const FeatureDisabledNote = styled.div`
  font-size: 0.8em;
  color: ${colors.greyXD};
  margin-left: 28px;
  margin-top: 2px;
`;

export const ThresholdToggleGroup = styled.div`
  display: flex;
  background-color: ${colors.white};
  border: 1px solid ${colors.greyL};
  border-radius: 6px;
  padding: 2px;
  gap: 0;
`;

export const ThresholdToggleButton = styled.button`
  flex: 1;
  padding: 6px 12px;
  font-size: 0.85em;
  font-weight: 500;
  color: ${props => props.active ? colors.black : colors.greyXD};
  background-color: ${props => props.active ? `${props.theme.primaryColor}20` : 'transparent'};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${props => props.active ? '0 1px 2px rgba(0, 0, 0, 0.05)' : 'none'};
  
  &:hover {
    color: ${props => props.active ? colors.black : colors.black};
    background-color: ${props => props.active ? `${props.theme.primaryColor}20` : colors.greyXXL};
  }
  
  &:focus {
    outline: none;
  }
`;

export const CompactInfoBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  background-color: rgba(239, 240, 238, 0.5);
  border: 1px solid ${colors.greyL};
  border-radius: 8px;
  font-size: 0.85em;
  
  @media (min-width: 640px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

export const CompactInfoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${colors.greyXD};
  font-weight: 500;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

export const CompactInfoValues = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-family: 'Monaco', 'Courier New', monospace;
  color: ${colors.black};
  
  @media (min-width: 640px) {
    flex-direction: row;
    gap: 24px;
  }
`;

export const CompactInfoDivider = styled.span`
  display: none;
  color: ${colors.grey};
  
  @media (min-width: 640px) {
    display: inline;
  }
`;

export const ConfigSectionLabel = styled.label`
  display: block;
  font-size: 0.75em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${colors.greyXD};
  margin-bottom: 12px;
`;

export const ConfigGridRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  border-top: 1px solid rgba(229, 232, 225, 0.5);
  
  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const ConfigField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ModernCouplingSection = styled.div`
  padding: 20px;
  margin-bottom: 32px;
  background-color: ${colors.greyXL};
  border: 1px solid ${colors.greyL};
  border-radius: 12px;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const CouplingHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => props.isExpanded ? '20px' : '0'};
`;

export const CollapseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  color: ${colors.greyD};
  font-size: 0.85em;
  transition: color 0.2s ease;
  
  &:hover {
    color: ${colors.black};
  }
  
  svg {
    margin-left: 4px;
    transition: transform 0.2s ease;
    transform: ${props => props.isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'};
  }
`;

export const CollapsibleContent = styled.div`
  overflow: hidden;
  max-height: ${props => props.isExpanded ? '3000px' : '0'};
  opacity: ${props => props.isExpanded ? '1' : '0'};
  transition: max-height 0.3s ease, opacity 0.3s ease;
`;

export const DropdownOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10;
`;

export const VibrationCardContainer = styled.div`
  position: relative;
`;

// Improved Alarm Thresholds Display Components
export const ThresholdsDisplayContainer = styled.div`
  margin-top: 20px;
  padding: 12px;
  background-color: ${colors.white};
  border: 1px solid ${colors.greyL};
  border-radius: 8px;
  font-size: 0.85em;
`;

export const ThresholdsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: ${colors.greyXD};
  font-weight: 500;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

export const ThresholdsGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ThresholdGroupItem = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr auto;
  gap: 8px;
  align-items: baseline;
  padding: 8px 0;
  border-bottom: 1px solid rgba(229, 232, 225, 0.5);
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 4px;
  }
`;

export const ThresholdLabel = styled.span`
  font-weight: 600;
  color: ${colors.black};
  flex-shrink: 0;
  
  @media (max-width: 640px) {
    margin-bottom: 4px;
  }
`;

export const ThresholdValue = styled.span`
  font-family: 'Monaco', 'Courier New', monospace;
  color: ${colors.black};
  font-weight: 500;
`;

export const ThresholdBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.75em;
  font-weight: 600;
  text-transform: uppercase;
  background-color: ${props => props.level === 'critical' ? 'rgba(255, 92, 92, 0.1)' : 'rgba(243, 144, 10, 0.1)'};
  color: ${props => props.level === 'critical' ? colors.redL : colors.warning};
  white-space: nowrap;
  
  @media (max-width: 640px) {
    justify-self: start;
  }
`;

export const ThresholdDirections = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  grid-column: 2;
  
  @media (max-width: 640px) {
    grid-column: 1;
    margin-top: 4px;
  }
`;

export const DirectionItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.9em;
  
  &::before {
    content: '•';
    color: ${colors.greyD};
    margin-right: 2px;
  }
  
  &:first-child::before {
    display: none;
  }
`;

// Additional styled components for AlarmThresholdCouplingControl
export const CouplingContainer = styled.div`
  padding-left: 4px;
  padding-right: 4px;
  margin-bottom: 24px;
`;

export const ToggleSwitchWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  user-select: none;
  opacity: ${props => props.disabled ? 0.6 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
`;

export const LoadingIndicator = styled.span`
  margin-left: 8px;
  display: inline-flex;
  align-items: center;
  
  .ui.loader {
    margin-right: 6px;
  }
`;

export const LoadingText = styled.span`
  font-size: 12px;
  color: #666;
`;

export const WarningBoxWithMargin = styled(WarningBox)`
  margin-bottom: 20px;
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

export const WarningList = styled.ul`
  margin-top: 8px;
  margin-bottom: 0;
`;

export const WarningParagraph = styled.p`
  margin-top: 8px;
  margin-bottom: 0;
`;

export const SensorSectionContainer = styled.div`
  margin-bottom: 24px;
`;

export const SensorCardWrapper = styled(SensorSelectionCard)`
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.6 : 1};
`;

export const SensorCardLeftWrapper = styled(SensorCardLeft)`
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
`;

export const FeatureDropdownWrapper = styled.div`
  position: relative;
  z-index: 10;
`;

export const FeatureDropdownText = styled.span`
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const HelperText = styled.div`
  font-size: 0.85em;
  color: #999;
  font-style: italic;
  margin-top: 12px;
`;

export const HelpCursor = styled.span`
  cursor: help;
`;

export const SpeedBinDropdown = styled(Dropdown)`
  &&& {
    min-width: 100%;
    font-size: 0.85em;
  }
`;

export const ThresholdValueContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const CouplingDescriptionWrapper = styled(CouplingDescription)`
  margin-top: 12px;
  margin-left: 0;
`;
